import React, { useState, useEffect, useRef } from 'react';
import './BankDetails.css';
import { useUser } from '../context/UserContext';
import Swal from 'sweetalert2';
import { buildDiffPreview, fireAudit } from '../utils/auditClient';

const IFSC_REGEX = /^[A-Z]{4}0[A-Z0-9]{6}$/i;
const ACCOUNT_REGEX = /^[0-9]{6,18}$/;

const BankDetails = () => {
  const { selectedCenter } = useUser();
  const token = localStorage.getItem('auth-token');

  const [formData, setFormData] = useState({
    beneficiaryName: '',
    accountNumber: '',
    accountType: '',
    ifscCode: '',
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [error, setError] = useState(null);
  const [bankTransferEnabled, setBankTransferEnabled] = useState(false);
  const [editFlag, setEditFlag] = useState(false);

  const baselineRef = useRef({
    beneficiaryName: '',
    accountNumber: '',
    accountType: '',
    ifscCode: '',
    bankTransferEnabled: false,
  });

  const snapshot = (overrides = {}) => ({
    beneficiaryName: (formData.beneficiaryName || '').trim(),
    accountNumber: (formData.accountNumber || '').trim(),
    accountType: (formData.accountType || '').trim(),
    ifscCode: (formData.ifscCode || '').trim().toUpperCase(),
    bankTransferEnabled,
    ...overrides,
  });

  useEffect(() => {
    const fetchBankDetails = async () => {
      if (!selectedCenter) {
        setLoading(false);
        return;
      }

      const url = `${process.env.REACT_APP_API_ACADEMY_BANK_DETAILS}/${selectedCenter}/bank-details`;

      try {
        setError(null);
        const response = await fetch(url, {
          method: 'GET',
          headers: { 'Authorization': `Bearer ${token}` },
        });

        if (!response.ok) throw new Error('Failed to fetch bank details');

        const result = await response.json();
        if (result.success && result.data.bankDetailForm) {
          const { beneficiaryName, accountNumber, accountType, ifscCode } = result.data.bankDetailForm;
          setFormData({
            beneficiaryName: beneficiaryName || '',
            accountNumber: accountNumber || '',
            accountType: accountType || '',
            ifscCode: ifscCode || '',
          });
          setBankTransferEnabled(Boolean(result.data.bankTransferEnabled));
          setEditFlag(Boolean(result.data.editFlag));
          baselineRef.current = {
            beneficiaryName: beneficiaryName || '',
            accountNumber: accountNumber || '',
            accountType: accountType || '',
            ifscCode: (ifscCode || '').toUpperCase(),
            bankTransferEnabled: Boolean(result.data.bankTransferEnabled),
          };
        } else {
          baselineRef.current = {
            beneficiaryName: '',
            accountNumber: '',
            accountType: '',
            ifscCode: '',
            bankTransferEnabled: false,
          };
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchBankDetails();
  }, [selectedCenter]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData(prev => ({
      ...prev,
      [name]:
        name === 'ifscCode'
          ? value.toUpperCase().replace(/\s+/g, '')
          : value.replace(name === 'accountNumber' ? /\D/g : /$^/, '')
    }));
  };

  const validate = () => {
    const s = snapshot();
    if (!s.beneficiaryName || !s.accountNumber || !s.accountType || !s.ifscCode) {
      Swal.fire({
        icon: 'error',
        title: 'Missing Details',
        text: 'All fields are required. Please fill them out.',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000,
      });
      return false;
    }
    if (!ACCOUNT_REGEX.test(s.accountNumber)) {
      Swal.fire({
        icon: 'warning',
        title: 'Check Account Number',
        text: 'Account number should be 6–18 digits.',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000,
      });
      return false;
    }
    if (!IFSC_REGEX.test(s.ifscCode)) {
      Swal.fire({
        icon: 'warning',
        title: 'Invalid IFSC',
        text: 'IFSC should look like ABCD0XXXXXX.',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000,
      });
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!editFlag) return;
    if (!selectedCenter) return;
    if (!validate()) return;

    const url = process.env.REACT_APP_API_URL_BANK_DETAILS_POST;

    const newState = snapshot();
    const payload = {
      academyId: Number(selectedCenter),
      beneficiaryName: newState.beneficiaryName,
      accountNumber: newState.accountNumber,
      accountType: newState.accountType,
      ifscCode: newState.ifscCode,
    };

    const { digest, html } = buildDiffPreview(baselineRef.current || {}, { ...newState });
    if (!digest?.length) {
      Swal.fire({
        icon: 'info',
        title: 'Nothing to update',
        text: 'No differences detected.',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 2200,
      });
      return;
    }

    const confirm = await Swal.fire({
      title: 'Save these bank details?',
      html,
      showCancelButton: true,
      confirmButtonText: 'Save',
    });
    if (!confirm.isConfirmed) return;

    try {
      setSaving(true);
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ academyId: Number(selectedCenter), ...newState }),
      });

      const result = await response.json().catch(() => ({}));

      fireAudit({
        entity: 'academy_bank_details',
        affected_id: Number(selectedCenter),
        action: 'upsert',
        section: 'Academy Profile',
        subSection: 'Bank Details',
        businessApi: url,
        businessMethod: 'POST',
        oldPayload: baselineRef.current || {},
        newPayload: { academyId: Number(selectedCenter), ...newState },
        extra: { page: 'BankDetails' },
      });

      if (response.ok && (result.success !== false)) {
        Swal.fire({
          icon: 'success',
          title: 'Success!',
          text: 'Bank details saved successfully.',
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 3000,
        });
        baselineRef.current = { ...newState };
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Update Failed',
          text: result?.message || 'Something went wrong.',
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 3000,
        });
      }
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Network Error',
        text: 'Could not update bank details. Try again later.',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleToggleBankTransfer = async () => {
    if (!selectedCenter) return;

    const nextEnabled = !bankTransferEnabled;
    const apiUrl = `${process.env.REACT_APP_API_TOGGLE_BANK_TRANSFER}/${selectedCenter}/${bankTransferEnabled ? 'disable' : 'enable'}-bank-transfer`;

    const { html, digest } = buildDiffPreview(
      { bankTransferEnabled: baselineRef.current.bankTransferEnabled },
      { bankTransferEnabled: nextEnabled }
    );

    if (!digest?.length) return;

    const confirm = await Swal.fire({
      title: `${nextEnabled ? 'Enable' : 'Disable'} Bank Transfer?`,
      html,
      showCancelButton: true,
      confirmButtonText: nextEnabled ? 'Enable' : 'Disable',
    });
    if (!confirm.isConfirmed) return;

    try {
      setToggling(true);
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });

      const responseData = await response.json().catch(() => ({}));

      fireAudit({
        entity: 'academy_bank_details',
        affected_id: Number(selectedCenter),
        action: 'toggle_bank_transfer',
        section: 'Academy Profile',
        subSection: 'Bank Details',
        businessApi: apiUrl,
        businessMethod: 'POST',
        oldPayload: { bankTransferEnabled: baselineRef.current.bankTransferEnabled },
        newPayload: { bankTransferEnabled: nextEnabled },
        extra: { page: 'BankDetails' },
      });

      if (!response.ok) throw new Error(responseData.message || 'Failed to update bank transfer status');

      setBankTransferEnabled(nextEnabled);
      baselineRef.current.bankTransferEnabled = nextEnabled;

      Swal.fire({
        icon: 'success',
        title: 'Updated',
        text: `Bank Transfer ${nextEnabled ? 'Enabled' : 'Disabled'}.`,
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 2500,
      });
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Update Failed',
        text: err.message || 'Could not toggle bank transfer.',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000,
      });
    } finally {
      setToggling(false);
    }
  };

  return (
    <div className="enrollment-container">
      {loading && (
        <div className="premium-loader">
          <div className="progress-track"><div className="progress-bar"></div></div>
          <div className="status-text">
            <span>L</span><span>O</span><span>A</span><span>D</span><span>I</span><span>N</span><span>G</span>
          </div>
        </div>
      )}
      {error && <div className="error-message">{error}</div>}

      {!loading && (
        <form onSubmit={(e) => e.preventDefault()}>
          <div className="floating-select-form" style={{ width: '98%' }}>
            <input
              type="text"
              name="beneficiaryName"
              className={`walkin-form ${formData.beneficiaryName ? "has-value" : ""}`}
              value={formData.beneficiaryName}
              onChange={handleChange}
              placeholder="Enter Beneficiary Name"
              disabled={!editFlag}
              autoComplete="off"
            />
            {formData.beneficiaryName && <label htmlFor="beneficiaryName">Beneficiary Name</label>}
          </div>

          <div className="floating-select-form" style={{ width: '98%' }}>
            <input
              type="text"
              name="accountNumber"
              className={`walkin-form ${formData.accountNumber ? "has-value" : ""}`}
              value={formData.accountNumber}
              onChange={handleChange}
              placeholder="Enter Account Number"
              disabled={!editFlag}
              inputMode="numeric"
              autoComplete="off"
            />
            {formData.accountNumber && <label htmlFor="accountNumber">Account Number</label>}
          </div>

          <div className="floating-select-form" style={{ width: '98%' }}>
            <select
              name="accountType"
              className={`walkin-form ${formData.accountType ? "has-value" : ""}`}
              value={formData.accountType}
              onChange={handleChange}
              disabled={!editFlag}
            >
              <option value="">Select Account Type</option>
              <option value="SAVING">Savings</option>
              <option value="CURRENT">Current</option>
            </select>
            {formData.accountType && <label htmlFor="accountType">Account Type</label>}
          </div>

          <div className="floating-select-form" style={{ width: '98%' }}>
            <input
              type="text"
              name="ifscCode"
              className={`walkin-form ${formData.ifscCode ? "has-value" : ""}`}
              value={formData.ifscCode}
              onChange={handleChange}
              placeholder="Enter IFSC Code"
              disabled={!editFlag}
              autoComplete="off"
            />
            {formData.ifscCode && <label htmlFor="ifscCode">IFSC Code</label>}
          </div>

          {editFlag && (
            <button
              type="button"
              onClick={handleSubmit}
              className="submit-button"
              disabled={saving}
            >
              {saving ? 'Saving…' : 'Submit'}
            </button>
          )}

          {!editFlag && (
            <div className="bank-transfer-container">
              <button
                type="button"
                onClick={handleToggleBankTransfer}
                className={`bank-transfer-button ${bankTransferEnabled ? 'disable' : 'enable'}`}
                disabled={toggling}
              >
                {toggling
                  ? (bankTransferEnabled ? 'Disabling…' : 'Enabling…')
                  : (bankTransferEnabled ? 'Disable Bank Transfer' : 'Enable Bank Transfer')}
              </button>
              {!bankTransferEnabled && <span className="status-text-bank">Bank Transfer is Disabled</span>}
            </div>
          )}
        </form>
      )}
    </div>
  );
};

export default BankDetails;
